import {
  bool,
  elementType,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from "prop-types";
import { forwardRef, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrConfirmCancel from "UI/FnrConfirmCancel/FnrConfirmCancel";
import FnrIcon from "UI/FnrIcon/FnrIcon";
import FnrIconButton from "UI/FnrIconButton/FnrIconButton";
import FnrOpacityToggle from "UI/FnrOpacityToggle/FnrOpacityToggle";
import { FnrPopover } from "UI/FnrPopover2";
import FnrTextButton from "UI/FnrTextButton";
import { colors } from "UI/colors";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap/FnrVerticalGap";

const SFnrVerticalGap = styled(FnrVerticalGap)`
  max-width: ${({ $width }) => {
    return `${$width}px`;
  }};
`;

const SFnrIconButton = styled(FnrIconButton)`
  background-color: ${colors.fnGray100};
`;

const icon = "fa-trash-alt far";

const VariantSwitch = forwardRef(
  (
    {
      variant,
      buttonText,
      showPopover,
      ariaLabel,
      isLoading,
      Button,
      buttonProps,
      ...rest
    },
    ref
  ) => {
    if (Button)
      return (
        <Button
          {...buttonProps}
          ref={ref}
          aria-label={ariaLabel}
          type="button"
          showPopover={showPopover}
          {...rest}
        />
      );
    switch (variant) {
      case "textButton":
        return (
          <FnrTextButton
            ref={ref}
            icon={icon}
            aria-label={ariaLabel}
            type="button" // Do not submit if in a form
            isLoading={isLoading} // TODO: add loading state to FnrIconButton
            {...rest}
          >
            {buttonText}
          </FnrTextButton>
        );
      case "iconButton":
        return (
          <FnrIconButton
            ref={ref}
            icon={icon}
            tooltipText={buttonText}
            aria-label={ariaLabel}
            variant="danger"
            type="button" // Do not submit if in a form
            isLoading={isLoading} // TODO: add loading state to FnrIconButton
            {...rest}
          />
        );
      case "iconButtonWithBackground":
        return (
          <SFnrIconButton
            ref={ref}
            icon={icon}
            tooltipText={buttonText}
            aria-label={ariaLabel}
            variant="error"
            type="button" // Do not submit if in a form
            isLoading={isLoading}
            {...rest}
          />
        );
      default:
        return (
          <FnrButton
            aria-label={ariaLabel}
            ref={ref}
            color={showPopover ? "grey" : "red"}
            type="button" // Do not submit if in a form
            loading={isLoading}
            {...rest}
          >
            <FnrHorizontalGap>
              <FnrIcon icon={icon} />
              {buttonText}
            </FnrHorizontalGap>
          </FnrButton>
        );
    }
  }
);

VariantSwitch.propTypes = {
  variant: oneOf([
    "iconButton",
    "default",
    "iconButtonWithBackground",
    "textButton",
  ]),
  buttonText: string,
  showPopover: bool,
  ariaLabel: string.isRequired,
  isLoading: bool,
  children: node,
  Button: elementType,
  buttonProps: object,
};

const FnrDeleteButton = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {string} param0.confirmText - Text displayed before actually deleting. Leave this undefined to execute the delete funtion without confirmation fro the user
   * @param {string} param0.confirmButtonText - The text that will appear in the delete button in the confirmation popover
   * @param {string} param0.cancelButtonText - The text that will appear in the cancel button in the confirmation popover
   * @param {function} param0.onClick - The function to be executed.
   * @param {string} param0.buttonText - Leave this undefined to get a trash can icon. Will display text instead when passed in.
   * @param {boolean} param0.isLoading - Displays loading indicator inside main button. Via via "...rest".
   * @param {"default"|"iconButton"|"iconButtonWithBackground"|"textButton"} param0.variant - Nuances the presentation of the main button.
   * @param {function|import("react").ReactNode} param0.AnimationComponent - Defaults to opacity toggle. Pass in any component accepting "show"
   * @param {object} param0.animationComponentProps - props passed to the AnimationComponent
   * @param {boolean} param0.show - Hides the delete button via the AnimationComponent
   * @param {number} param0.popoverWidth - changes the max-width of the popover
   * @param {function} param0.cancelSideEffect - function to be executed when the cancel button is clicked
   * @param {function} param0.onPopoverOpen - if the popover opens (or, if there is confirm text), this function will fire
   * @param {string} param0.deleteIcon - don't like the trash can? The pass in something different.
   * @param {import("react").ElementType} param0.Button - Pass in your own button component
   * @param {object} param0.buttonProps - Pass in the button props (onClick will be overwritten)
   * @param {import("react").Ref} param0.popoverRef - passed down to the popover container
   * @param {boolean} param0.disableConfirmButton - Disables the confirm button in FnrConfirmDelete
   * @param {boolean} parm0.hideConfirmButton - Hides the confirm button in FnrConfirmDelete
   */
  (
    {
      confirmText,
      confirmButtonText = "Yes",
      cancelButtonText,
      onClick,
      buttonText,
      variant = "default",
      "aria-label": ariaLabel = "delete-button",
      show = true,
      AnimationComponent = FnrOpacityToggle,
      animationComponentProps = {},
      popoverWidth = 400,
      cancelSideEffect,
      onPopoverOpen,
      deleteIcon = "fa-trash-alt far",
      popoverRef,
      Button,
      buttonProps,
      disableConfirmButton,
      hideConfirmButton,
      dataCancelClickAway,
      ...rest
    },
    refArg
  ) => {
    const [showPopover, setShowPopover] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
      setIsMounted(true);
      return () => setIsMounted(false);
    }, []);
    const localRef = useRef();
    const ref = refArg || localRef;

    const handleFirstClick = () => {
      if (confirmText) {
        setShowPopover(!showPopover);
        if (onPopoverOpen) onPopoverOpen();
      } else {
        onClick();
      }
    };

    const closePopover = () => {
      if (isMounted) {
        setShowPopover(false);
        if (cancelSideEffect) cancelSideEffect();
      }
    };

    const handleSecondclick = () => {
      onClick();
      closePopover();
    };

    return (
      <AnimationComponent
        {...animationComponentProps}
        show={show || showPopover}
      >
        <VariantSwitch
          onClick={handleFirstClick}
          data-cy="DeleteButton"
          ref={ref}
          showPopover={showPopover}
          buttonText={buttonText}
          variant={variant}
          ariaLabel={ariaLabel}
          Button={Button}
          buttonProps={buttonProps}
          {...rest}
        />
        <FnrPopover
          buttonRef={ref}
          show={showPopover}
          onShouldClose={closePopover}
          onClose={closePopover}
          color="red"
          variant="container"
          dataCancelClickAway={dataCancelClickAway}
        >
          <SFnrVerticalGap
            ref={popoverRef}
            $width={popoverWidth}
            data-cy="FnrDeleteButton_Popover_VerticalGap"
          >
            {confirmText}
            <FnrConfirmCancel
              icon={deleteIcon}
              variant="danger"
              confirm={handleSecondclick}
              confirmText={confirmButtonText}
              cancelText={cancelButtonText}
              cancel={closePopover}
              disableConfirm={disableConfirmButton}
              hideConfirmButton={hideConfirmButton}
              autoFocus
            />
          </SFnrVerticalGap>
        </FnrPopover>
      </AnimationComponent>
    );
  }
);

FnrDeleteButton.propTypes = {
  confirmText: node,
  onClick: func.isRequired,
  buttonText: string,
  confirmButtonText: string,
  cancelButtonText: string,
  loading: bool,
  variant: oneOf([
    "default",
    "iconButton",
    "iconButtonWithBackground",
    "textButton",
  ]),
  "aria-label": string,
  show: bool,
  AnimationComponent: oneOfType([func, object]),
  animationComponentProps: object,
  popoverWidth: number,
  cancelSideEffect: func,
  onPopoverOpen: func,
  deleteIcon: string,
  popoverRef: object,
  Button: elementType,
  buttonProps: object,
  disableConfirmButton: bool,
  hideConfirmButton: bool,
  dataCancelClickAway: string,
};

export default FnrDeleteButton;
